<script setup>
/* eslint-disable no-console */
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useAlert } from 'dashboard/composables';
import { resolveOT } from 'dashboard/api/cea';
import ServiceOrdersAPI from 'dashboard/api/serviceOrders';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  primaryOrder: {
    type: Object,
    default: null,
  },
  selectedOrders: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['close', 'merged']);

const { t } = useI18n();

const dialogRef = ref(null);
const isSubmitting = ref(false);
const searchQuery = ref('');
const searchResults = ref([]);
const isSearching = ref(false);
const aquasisWarnings = ref([]);

// Orders in the merge set (excluding the primary)
const mergeOrders = ref([]);
const selectedPrimaryId = ref(null);

// Mode: 'detail' = from order detail page, 'list' = from orders list
const mode = computed(() => {
  if (props.primaryOrder && !props.selectedOrders.length) return 'detail';
  return 'list';
});

const allOrders = computed(() => {
  if (mode.value === 'detail') {
    return [props.primaryOrder, ...mergeOrders.value];
  }
  return [...props.selectedOrders, ...mergeOrders.value].filter(
    (o, i, arr) => arr.findIndex(x => x.id === o.id) === i
  );
});

const primaryId = computed(() => {
  if (mode.value === 'detail') return props.primaryOrder?.id;
  return selectedPrimaryId.value;
});

const childOrders = computed(() => {
  return allOrders.value.filter(o => o.id !== primaryId.value);
});

const canSubmit = computed(() => {
  return primaryId.value && childOrders.value.length > 0 && !isSubmitting.value;
});

const searchOrders = async () => {
  if (!searchQuery.value.trim()) {
    searchResults.value = [];
    return;
  }
  try {
    isSearching.value = true;
    const response = await ServiceOrdersAPI.get({
      search: searchQuery.value,
      page: 1,
    });
    const existing = allOrders.value.map(o => o.id);
    searchResults.value = (response.data.payload || []).filter(
      o => !existing.includes(o.id) && o.status !== 'merged'
    );
  } catch (error) {
    console.error('Error searching orders:', error);
    searchResults.value = [];
  } finally {
    isSearching.value = false;
  }
};

const addOrder = order => {
  mergeOrders.value.push(order);
  searchResults.value = searchResults.value.filter(o => o.id !== order.id);
  searchQuery.value = '';
};

const removeOrder = orderId => {
  mergeOrders.value = mergeOrders.value.filter(o => o.id !== orderId);
  if (selectedPrimaryId.value === orderId) {
    selectedPrimaryId.value = null;
  }
};

const getStatusLabel = status => {
  const labels = {
    pending: t('ORDERS.STATUS_PENDING'),
    in_progress: t('ORDERS.STATUS_IN_PROGRESS'),
    completed: t('ORDERS.STATUS_COMPLETED'),
    cancelled: t('ORDERS.STATUS_CANCELLED'),
    merged: t('ORDERS.STATUS_MERGED'),
  };
  return labels[status] || status;
};

const getStatusColor = status => {
  const colors = {
    pending: 'bg-n-amber-9/10 text-n-amber-11',
    in_progress: 'bg-n-brand/10 text-n-blue-text',
    completed: 'bg-n-teal-9/10 text-n-teal-11',
    cancelled: 'bg-n-ruby-9/10 text-n-ruby-11',
    merged: 'bg-n-slate-9/10 text-n-slate-11',
  };
  return colors[status] || 'bg-n-slate-9/10 text-n-slate-11';
};

const nullifyChildInAquasis = async child => {
  if (!child.aquasis_order_id) return true;

  try {
    const now = new Date().toISOString();
    const payload = {
      operationalSiteID: '',
      installationID: '',
      systemOrigin: '',
      otClass: '0',
      otOrigin: child.aquasis_order_id,
      endDateOt: now,
      endLastTaskOt: now,
      finalSolution: '',
      solutionDescription: '',
      nonExecutionMotive: t('ORDERS.MERGE.AQUASIS_NULLIFY_MOTIVE', {
        id: primaryId.value,
      }),
      executorIdentifier: '',
      executorName: '',
      companyExecutorIdentifier: '',
      companyExecutorName: '',
      transmitterInstalled: '0',
      language: 'es',
      suspensionLevel: '0',
      longitude: '',
      latitude: '',
      coordinatesType: '',
      codificationType: '',
      captureDate: '',
      resolutionElements: [],
    };

    const xmlDoc = await resolveOT(payload);
    const returnEl =
      xmlDoc.getElementsByTagName('return')[0] ||
      xmlDoc.getElementsByTagName('resolveOTReturn')[0] ||
      xmlDoc.getElementsByTagName('resolveOTResponse')[0];

    const resultText = returnEl?.textContent?.trim()?.toLowerCase();
    return resultText === 'true' || resultText === '1';
  } catch (error) {
    console.error(`Error nullifying order ${child.id} in Aquasis:`, error);
    return false;
  }
};

const resetState = () => {
  mergeOrders.value = [];
  selectedPrimaryId.value = null;
  searchQuery.value = '';
  searchResults.value = [];
  aquasisWarnings.value = [];
};

const handleClose = () => {
  resetState();
  emit('close');
};

const handleSubmit = async () => {
  if (!canSubmit.value) return;

  try {
    isSubmitting.value = true;
    aquasisWarnings.value = [];

    // Step 1: Nullify children in Aquasis
    const aquasisChildren = childOrders.value.filter(o => o.aquasis_order_id);
    const results = await Promise.all(
      aquasisChildren.map(async child => {
        const success = await nullifyChildInAquasis(child);
        return { child, success };
      })
    );
    results
      .filter(r => !r.success)
      .forEach(r => {
        aquasisWarnings.value.push(
          t('ORDERS.MERGE.AQUASIS_NULLIFY_ERROR', {
            id: r.child.id,
            aquasis_id: r.child.aquasis_order_id,
          })
        );
      });

    // Step 2: Merge locally
    const childIds = childOrders.value.map(o => o.id);
    await ServiceOrdersAPI.merge(primaryId.value, childIds);

    if (aquasisWarnings.value.length > 0) {
      useAlert(
        t('ORDERS.MERGE.PARTIAL_SUCCESS', {
          count: aquasisWarnings.value.length,
        })
      );
    } else {
      useAlert(t('ORDERS.MERGE.SUCCESS'));
    }

    emit('merged');
    handleClose();
  } catch (error) {
    console.error('Error merging orders:', error);
    useAlert(error.response?.data?.error || t('ORDERS.MERGE.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

watch(
  () => props.show,
  newVal => {
    if (newVal) {
      resetState();
      if (mode.value === 'list' && props.selectedOrders.length > 0) {
        selectedPrimaryId.value = props.selectedOrders[0].id;
      }
      dialogRef.value?.open();
    } else {
      dialogRef.value?.close();
    }
  }
);
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="lg"
    :title="$t('ORDERS.MERGE.TITLE')"
    overflow-y-auto
    :is-loading="isSubmitting"
    :confirm-button-label="
      isSubmitting ? $t('ORDERS.MERGE.SUBMITTING') : $t('ORDERS.MERGE.TITLE')
    "
    :disable-confirm-button="!canSubmit"
    @close="handleClose"
    @confirm="handleSubmit"
  >
    <div class="flex flex-col gap-4">
      <!-- Orders table -->
      <div v-if="allOrders.length > 0">
        <h4 class="text-sm font-medium text-n-slate-11 mb-2">
          {{ $t('ORDERS.MERGE.ORDERS_TO_MERGE') }}
        </h4>
        <div class="overflow-x-auto rounded-lg border border-n-weak">
          <table class="w-full text-sm">
            <thead class="bg-n-solid-3">
              <tr>
                <th
                  v-if="mode === 'list'"
                  class="px-3 py-2 text-left text-xs text-n-slate-11"
                >
                  {{ $t('ORDERS.MERGE.SELECT_PRIMARY') }}
                </th>
                <th class="px-3 py-2 text-left text-xs text-n-slate-11">
                  {{ $t('ORDERS.MERGE.COLUMN_ID') }}
                </th>
                <th class="px-3 py-2 text-left text-xs text-n-slate-11">
                  {{ $t('ORDERS.MERGE.COLUMN_AQUASIS') }}
                </th>
                <th class="px-3 py-2 text-left text-xs text-n-slate-11">
                  {{ $t('ORDERS.MERGE.COLUMN_TICKET') }}
                </th>
                <th class="px-3 py-2 text-left text-xs text-n-slate-11">
                  {{ $t('ORDERS.MERGE.COLUMN_STATUS') }}
                </th>
                <th class="px-3 py-2 text-center text-xs text-n-slate-11">
                  {{ $t('ORDERS.ACTIONS') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="order in allOrders"
                :key="order.id"
                class="border-t border-n-weak"
                :class="{
                  'bg-n-brand/5': order.id === primaryId,
                }"
              >
                <td v-if="mode === 'list'" class="px-3 py-2">
                  <input
                    type="radio"
                    :checked="selectedPrimaryId === order.id"
                    name="primary_order"
                    class="accent-n-brand"
                    @change="selectedPrimaryId = order.id"
                  />
                </td>
                <td class="px-3 py-2 font-mono text-n-slate-12">
                  #{{ order.id }}
                  <span
                    v-if="order.id === primaryId"
                    class="ml-1 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-n-brand/10 text-n-blue-text"
                  >
                    {{ $t('ORDERS.MERGE.PRIMARY') }}
                  </span>
                </td>
                <td class="px-3 py-2 font-mono text-n-brand">
                  {{ order.aquasis_order_id || '-' }}
                </td>
                <td class="px-3 py-2 text-n-slate-12">
                  {{ order.ticket?.folio || '-' }}
                </td>
                <td class="px-3 py-2">
                  <span
                    :class="getStatusColor(order.status)"
                    class="px-2 py-0.5 text-xs font-medium rounded-full"
                  >
                    {{ getStatusLabel(order.status) }}
                  </span>
                </td>
                <td class="px-3 py-2 text-center">
                  <button
                    v-if="
                      order.id !== primaryOrder?.id &&
                      !(
                        mode === 'list' &&
                        selectedOrders.some(o => o.id === order.id)
                      )
                    "
                    class="text-n-ruby-11 hover:text-n-ruby-12 text-xs"
                    @click="removeOrder(order.id)"
                  >
                    {{ $t('ORDERS.MERGE.REMOVE') }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Search to add orders -->
      <div>
        <h4 class="text-sm font-medium text-n-slate-11 mb-2">
          {{ $t('ORDERS.MERGE.ADD_ORDERS') }}
        </h4>
        <div class="flex items-center gap-2">
          <div
            class="flex items-center gap-2 flex-1 px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak"
          >
            <span class="i-lucide-search text-n-slate-11 shrink-0" />
            <input
              v-model="searchQuery"
              type="text"
              :placeholder="$t('ORDERS.MERGE.SEARCH_PLACEHOLDER')"
              class="w-full bg-transparent border-none outline-none text-n-slate-12 placeholder-n-slate-10 text-sm"
              autocomplete="off"
              @input="searchOrders"
            />
          </div>
          <Spinner v-if="isSearching" :size="16" />
        </div>

        <!-- Search results -->
        <div
          v-if="searchResults.length > 0"
          class="mt-2 max-h-48 overflow-auto rounded-lg border border-n-weak"
        >
          <div
            v-for="result in searchResults"
            :key="result.id"
            class="flex items-center justify-between px-3 py-2 hover:bg-n-alpha-1 cursor-pointer border-b border-n-weak last:border-b-0"
            @click="addOrder(result)"
          >
            <div class="flex items-center gap-3">
              <span class="font-mono text-sm text-n-slate-12">
                #{{ result.id }}
              </span>
              <span
                v-if="result.aquasis_order_id"
                class="font-mono text-xs text-n-brand"
              >
                {{ result.aquasis_order_id }}
              </span>
              <span v-if="result.ticket" class="text-xs text-n-slate-11">
                {{ result.ticket.folio }}
              </span>
            </div>
            <span
              :class="getStatusColor(result.status)"
              class="px-2 py-0.5 text-xs font-medium rounded-full"
            >
              {{ getStatusLabel(result.status) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Warnings -->
      <div
        v-if="childOrders.some(o => o.aquasis_order_id)"
        class="bg-n-amber-3 rounded-lg p-3 border border-n-amber-6"
      >
        <div class="flex items-start gap-2">
          <span
            class="i-lucide-alert-triangle text-n-amber-11 mt-0.5 shrink-0"
          />
          <p class="text-xs text-n-amber-11">
            {{ $t('ORDERS.MERGE.AQUASIS_WARNING') }}
          </p>
        </div>
      </div>
    </div>
  </Dialog>
</template>
